import React, { useMemo } from 'react';
import { Icon } from '@iconify/react';
import { AppCheckbox } from '../ui';
import {
  ANNEE_OPTIONS,
  CANAUX_OPTIONS,
  MOIS_OPTIONS,
} from '../../utils/kkpCronUi';

const MediaPlanningFilters = ({ filters, onChange, meta, commerciaux, hideStatuts = false }) => {
  const updateFilter = (key, value) => {
    onChange({ ...filters, [key]: value });
  };

  const onlyOwnCommercialLines = Boolean(filters.only_own_commercial_lines);

  const toggleOnlyOwnCommercialLines = (checked) => {
    onChange({
      ...filters,
      only_own_commercial_lines: checked,
      commercial_id: checked ? '' : filters.commercial_id,
    });
  };

  const toggleCanal = (canalId) => {
    const current = filters.canaux || [];
    const next = current.includes(canalId)
      ? current.filter((c) => c !== canalId)
      : [...current, canalId];
    updateFilter('canaux', next);
  };

  const toggleArrayValue = (key, value) => {
    const current = filters[key] || [];
    let next = current.includes(value)
      ? current.filter((v) => v !== value)
      : [...current, value];
    if (key === 'mois') {
      next = [...next].map(Number).sort((a, b) => a - b);
    }
    updateFilter(key, next);
  };

  const formatOptions = useMemo(() => {
    if (!meta?.formats) return [];
    return meta.formats.map((f) => (typeof f === 'string' ? f : f.value || f.label || f.name || '')).filter(Boolean);
  }, [meta?.formats]);

  const contenuOptions = useMemo(() => {
    if (!meta?.contenus) return [];
    return meta.contenus.map((c) => (typeof c === 'string' ? c : c.value || c.label || c.name || '')).filter(Boolean);
  }, [meta?.contenus]);

  const statutOptions = useMemo(() => {
    if (!meta?.statuts) return [];
    return meta.statuts
      .map((s) => {
        if (typeof s === 'string') return { value: s, label: s, couleur: null };
        const value = s.value || s.label || s.name || '';
        if (!value) return null;
        return {
          value,
          label: s.label || value,
          couleur: s.couleur || null,
        };
      })
      .filter(Boolean);
  }, [meta?.statuts]);

  const showContenu = (filters.canaux || []).includes('mag');

  return (
    <div className="admin-cron-filters">
      <div className="admin-cron-filter-section">
        <div className="admin-cron-filter-section__head">
          <span className="admin-cron-filter-section__title">
            <Icon icon="mdi:account-filter-outline" width={18} />
            Destinataires
          </span>
        </div>
        <AppCheckbox
          id="cron-mp-only-own-commercial"
          switch
          checked={onlyOwnCommercialLines}
          onChange={(e) => toggleOnlyOwnCommercialLines(e.target.checked)}
          label="Chaque destinataire ne reçoit que ses lignes (commercial)"
        />
        <p className="text-sm text-secondary-light mb-0 mt-2">
          {onlyOwnCommercialLines
            ? 'Un mail personnalisé part à chaque personne, avec uniquement les dossiers où elle est le commercial. Les adresses sans commercial rattaché sont ignorées.'
            : 'Tous les destinataires reçoivent le même mail avec toutes les lignes filtrées.'}
        </p>
      </div>

      <div className="admin-cron-filter-section">
        <div className="admin-cron-filter-section__head">
          <span className="admin-cron-filter-section__title">
            <Icon icon="mdi:television-guide" width={18} />
            Supports
          </span>
        </div>
        <div className="d-flex flex-wrap gap-2">
          {CANAUX_OPTIONS.map((canal) => {
            const active = (filters.canaux || []).includes(canal.id);
            return (
              <button
                key={canal.id}
                type="button"
                className="admin-cron-filter-pill admin-cron-filter-pill--canal"
                style={active
                  ? { backgroundColor: canal.color, borderColor: canal.color, color: '#fff' }
                  : {
                    backgroundColor: `${canal.color}14`,
                    borderColor: `${canal.color}66`,
                    color: canal.color,
                  }}
                aria-pressed={active}
                onClick={() => toggleCanal(canal.id)}
              >
                <Icon icon={active ? 'mdi:check-circle' : canal.icon} width={18} />
                {canal.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="admin-cron-filter-section">
        <div className="row g-3">
          <div className="col-md-3">
            <div className="admin-cron-filter-section__head">
              <span className="admin-cron-filter-section__title">
                <Icon icon="mdi:calendar" width={18} />
                Année
              </span>
            </div>
            <select
              className="form-select"
              value={filters.annee || ''}
              onChange={(e) => updateFilter('annee', e.target.value ? Number(e.target.value) : '')}
            >
              <option value="">Toutes</option>
              {ANNEE_OPTIONS.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>

          <div className="col-md-9">
            <div className="admin-cron-filter-section__head">
              <span className="admin-cron-filter-section__title">
                <Icon icon="mdi:calendar-range" width={18} />
                Mois
              </span>
              {(filters.mois || []).length > 0 && (
                <button
                  type="button"
                  className="btn btn-sm btn-link text-secondary p-0"
                  onClick={() => updateFilter('mois', [])}
                  title="Tout effacer"
                >
                  Effacer
                </button>
              )}
            </div>
            <div className="d-flex flex-wrap gap-2">
              {MOIS_OPTIONS.map((m) => {
                const active = (filters.mois || []).includes(m.value);
                return (
                  <button
                    key={m.value}
                    type="button"
                    className={`admin-cron-filter-pill ${active ? 'is-active' : ''}`}
                    aria-pressed={active}
                    onClick={() => toggleArrayValue('mois', m.value)}
                  >
                    {m.label.substring(0, 3)}
                  </button>
                );
              })}
            </div>
            {(filters.mois || []).length === 0 && (
              <p className="text-sm text-secondary-light mb-0 mt-2">
                Aucun mois = M+1 (septembre → octobre). Dès que la date butoir du mois en cours est dépassée, ça passe à M+2.
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="admin-cron-filter-section">
        <div className="row g-3">
          {!onlyOwnCommercialLines && (
            <div className="col-md-6">
              <div className="admin-cron-filter-section__head">
                <span className="admin-cron-filter-section__title">
                  <Icon icon="mdi:account-tie" width={18} />
                  Commercial
                </span>
              </div>
              <select
                className="form-select"
                value={filters.commercial_id || ''}
                onChange={(e) => updateFilter('commercial_id', e.target.value)}
              >
                <option value="">Tous</option>
                {(commerciaux || []).map((c) => (
                  <option key={c.id} value={c.id}>{c.nom || c.fullName || c.email}</option>
                ))}
              </select>
            </div>
          )}

          <div className={onlyOwnCommercialLines ? 'col-12' : 'col-md-6'}>
            <div className="admin-cron-filter-section__head">
              <span className="admin-cron-filter-section__title">
                <Icon icon="mdi:account-search" width={18} />
                Client
              </span>
            </div>
            <input
              className="form-control"
              value={filters.search || ''}
              onChange={(e) => updateFilter('search', e.target.value)}
              placeholder="Rechercher un client…"
            />
          </div>
        </div>
      </div>

      {!hideStatuts && (
        <StatutPhasePicker
          options={statutOptions}
          selected={filters.statut_phase || []}
          onToggle={(value) => toggleArrayValue('statut_phase', value)}
          onClear={() => updateFilter('statut_phase', [])}
          hint="Cliquez sur les statuts à inclure. Il en faut au moins un."
        />
      )}

      {(formatOptions.length > 0 || (showContenu && contenuOptions.length > 0)) && (
        <div className="admin-cron-filter-section admin-cron-filter-section--last">
          <div className="row g-3">
            {formatOptions.length > 0 && (
              <div className={showContenu && contenuOptions.length > 0 ? 'col-lg-7' : 'col-12'}>
                <div className="admin-cron-filter-section__head">
                  <span className="admin-cron-filter-section__title">
                    <Icon icon="mdi:format-size" width={18} />
                    Format
                  </span>
                </div>
                <div className="d-flex flex-wrap gap-2">
                  {formatOptions.map((f) => {
                    const active = (filters.format || []).includes(f);
                    return (
                      <button
                        key={f}
                        type="button"
                        className={`admin-cron-filter-pill ${active ? 'is-active' : ''}`}
                        aria-pressed={active}
                        onClick={() => toggleArrayValue('format', f)}
                      >
                        {f}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {showContenu && contenuOptions.length > 0 && (
              <div className={formatOptions.length > 0 ? 'col-lg-5' : 'col-12'}>
                <div className="admin-cron-filter-section__head">
                  <span className="admin-cron-filter-section__title">
                    <Icon icon="mdi:text-box-outline" width={18} />
                    Contenu (MAG)
                  </span>
                </div>
                <div className="d-flex flex-wrap gap-2">
                  {contenuOptions.map((c) => {
                    const active = (filters.contenu || []).includes(c);
                    return (
                      <button
                        key={c}
                        type="button"
                        className={`admin-cron-filter-pill ${active ? 'is-active' : ''}`}
                        aria-pressed={active}
                        onClick={() => toggleArrayValue('contenu', c)}
                      >
                        {c}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const StatutPhasePicker = ({
  options = [],
  selected = [],
  onToggle,
  onClear,
  locked = false,
  hint = '',
  clearLabel = 'Effacer',
}) => (
  <div className="admin-cron-filter-section">
    <div className="admin-cron-filter-section__head">
      <span className="admin-cron-filter-section__title">
        <Icon icon="mdi:tag-multiple" width={18} />
        Statuts
      </span>
      {!locked && onClear && (selected.length > 0 || clearLabel !== 'Effacer') && (
        <button
          type="button"
          className="btn btn-sm btn-link text-secondary p-0"
          onClick={onClear}
          title={clearLabel}
        >
          {clearLabel}
        </button>
      )}
    </div>
    {hint ? <p className="text-sm text-secondary-light mb-2">{hint}</p> : null}
    {options.length === 0 ? (
      <p className="text-sm text-secondary-light mb-0">Chargement des statuts…</p>
    ) : (
      <div className="d-flex flex-wrap gap-2">
        {options.map((s) => {
          const active = selected.includes(s.value);
          return (
            <button
              key={s.value}
              type="button"
              className={`admin-cron-filter-pill ${active ? 'is-active' : ''}`}
              style={active && s.couleur
                ? { backgroundColor: s.couleur, borderColor: s.couleur, color: '#1a1a1a' }
                : undefined}
              aria-pressed={active}
              disabled={locked}
              onClick={() => !locked && onToggle?.(s.value)}
            >
              {s.label}
            </button>
          );
        })}
      </div>
    )}
  </div>
);

export default MediaPlanningFilters;
